/**
 * +glide: the sky puzzles' look (AirspaceDef.platforms / updrafts), built once per open map:
 *
 *  - wind chutes: two counter-rotating cylinders of spiralling translucent ribbons and streaks
 *    climbing the column (soft white–teal, additive, scrolled on the GPU from U.uTime), leaves and
 *    motes rising inside it (one Points draw per map), and a gentle pulsing ring of air at the base.
 *    Bright enough to pull the eye from across the valley, never a white-out;
 *  - platforms: floating mossy rock ledges, crystal-crowned rocks (the Pale Rise), wooden scaffolds
 *    on poles and ruined stone slabs: one instanced draw per part, outlined like the props. The top
 *    face sits exactly at the sim's platform top (the hero stands on it).
 *
 * Everything is static geometry or a stateless shader; `update` only fades the near chute glow.
 */
import * as THREE from 'three';
import type { AirspaceDef, PlatformDef, UpdraftDef } from '@keystone/sim';
import { U, ownedToonMaterial } from '../style';

const TEAL = new THREE.Color(0.62, 0.97, 0.9);

function hash(i: number): number {
  const s = Math.sin(i * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
}

/** The spiralling-ribbon column material (scroll speed and twist per layer). */
function ribbonMaterial(twist: number, speed: number, strength: number): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: { uTime: U.uTime, uFx: U.uFx, uColor: { value: TEAL }, uTwist: { value: twist }, uSpeed: { value: speed }, uStrength: { value: strength } },
    vertexShader: `varying vec2 vUv; varying float vH; varying vec3 vN; varying vec3 vV;
      void main(){ vUv = uv; vH = uv.y; vec4 w = modelMatrix * vec4(position, 1.0);
        vN = normalize(mat3(modelMatrix) * normal); vV = normalize(cameraPosition - w.xyz);
        gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: `uniform float uTime; uniform float uFx; uniform vec3 uColor; uniform float uTwist; uniform float uSpeed; uniform float uStrength;
      varying vec2 vUv; varying float vH; varying vec3 vN; varying vec3 vV;
      void main(){
        // Spiral bands climbing the column: three ribbons, broken into streaks.
        float s = fract(vUv.x * 3.0 + vUv.y * uTwist - uTime * uSpeed);
        float band = smoothstep(0.0, 0.12, s) * (1.0 - smoothstep(0.18, 0.42, s));
        float streak = 0.55 + 0.45 * sin(vUv.y * 41.0 - uTime * 7.0 + vUv.x * 19.0);
        float edge = pow(1.0 - abs(dot(vN, vV)), 1.4) * 0.6 + 0.4;
        float fade = smoothstep(0.0, 0.08, vH) * (1.0 - smoothstep(0.72, 1.0, vH));
        // A soft haze at the column's rims so it reads from across the valley, the ribbons over it.
        float haze = pow(1.0 - abs(dot(vN, vV)), 2.0) * 0.32 * (0.8 + 0.2 * sin(vUv.y * 9.0 - uTime * 2.0));
        float a = (band * streak * edge + haze) * fade * uStrength * min(1.2, uFx);
        gl_FragColor = vec4(uColor * a, a);
      }`,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
  });
}

/** Rising motes and leaves in every column (positions animate in the vertex shader). */
function moteMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: { uTime: U.uTime, uFx: U.uFx },
    vertexShader: `uniform float uTime; attribute vec4 aSeed; attribute vec3 aCol; varying float vA; varying vec3 vC; varying float vLeaf;
      void main(){
        // position = chute base (x, y, z); aSeed = (radius, height, phase, kind)
        float h = aSeed.y; float t = fract(aSeed.z + uTime * (0.07 + 0.05 * fract(aSeed.z * 7.0)));
        float ang = aSeed.z * 40.0 + uTime * (1.6 + fract(aSeed.z * 3.0)) + t * 6.0;
        float rr = aSeed.x * (0.25 + 0.75 * fract(aSeed.z * 13.0));
        vec3 p = position + vec3(cos(ang) * rr, t * h, sin(ang) * rr);
        vA = smoothstep(0.0, 0.08, t) * (1.0 - smoothstep(0.75, 1.0, t)); vC = aCol; vLeaf = aSeed.w;
        vec4 mv = modelViewMatrix * vec4(p, 1.0);
        gl_PointSize = (aSeed.w > 0.5 ? 190.0 : 95.0) / max(1.0, -mv.z);
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: `uniform float uFx; varying float vA; varying vec3 vC; varying float vLeaf;
      void main(){
        vec2 q = gl_PointCoord * 2.0 - 1.0;
        float d = vLeaf > 0.5 ? abs(q.x) * 1.8 + q.y * q.y : dot(q, q);
        if (d > 1.0) discard;
        float a = vA * (1.0 - d) * (vLeaf > 0.5 ? 0.9 : 0.75) * min(1.2, uFx);
        gl_FragColor = vec4(vC * a, a);
      }`,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
}

/** The pulsing ring of air at a chute's foot. */
function ringMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: { uTime: U.uTime, uFx: U.uFx, uColor: { value: TEAL } },
    vertexShader: `varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: `uniform float uTime; uniform float uFx; uniform vec3 uColor; varying vec2 vUv;
      void main(){
        float r = length(vUv * 2.0 - 1.0); if (r > 1.0) discard;
        float ring = smoothstep(0.55, 0.85, r) * (1.0 - smoothstep(0.9, 1.0, r));
        float wave = fract(r * 1.4 - uTime * 0.6); float w = smoothstep(0.0, 0.15, wave) * (1.0 - smoothstep(0.15, 0.5, wave)) * (1.0 - r);
        float a = (ring * 0.45 + w * 0.5) * min(1.2, uFx);
        gl_FragColor = vec4(uColor * a, a);
      }`,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
}

// ---- platform parts (unit size: radius 1, top at y = 0) --------------------------------------

function rockBody(): THREE.BufferGeometry {
  // A flat-topped, tapering rock: the top ring at y 0, ragged sides narrowing to a point below.
  const g = new THREE.CylinderGeometry(1, 0.15, 1.6, 9, 3);
  g.translate(0, -0.8, 0);
  const p = g.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < p.count; i++) {
    const y = p.getY(i);
    if (y > -0.01) continue;
    const k = 0.18 * (hash(i * 3.1) - 0.5);
    p.setXYZ(i, p.getX(i) * (1 + k), y * (1 + 0.25 * hash(i)), p.getZ(i) * (1 + k));
  }
  g.computeVertexNormals();
  return g;
}

const KIND_COL: Record<string, { body: number; top: number }> = {
  rock: { body: 0x8a8174, top: 0x6fa452 },
  crystal: { body: 0xa49cae, top: 0xd6d0e4 },
  scaffold: { body: 0x8b6a45, top: 0xb08a58 },
  ruin: { body: 0xc4bba8, top: 0xd9d2c0 },
};

interface PartSpec {
  geo: THREE.BufferGeometry;
  mat: THREE.Material;
  place: (p: PlatformDef, m: THREE.Matrix4, k: number) => boolean;
  count: (p: PlatformDef) => number;
  hull?: boolean;
}

export interface SkywaysView {
  group: THREE.Group;
  update(dtS: number): void;
  dispose(): void;
}

export function buildSkyways(as: AirspaceDef, groundY: (x: number, y: number) => number): SkywaysView | null {
  const plats = as.platforms ?? [];
  const chutes = as.updrafts ?? [];
  if (!plats.length && !chutes.length) return null;
  const group = new THREE.Group();
  group.name = 'skyways';
  const owned: { dispose(): void }[] = [];
  const own = <T extends { dispose(): void }>(x: T): T => (owned.push(x), x);

  // ---- chutes ----------------------------------------------------------------------------------
  if (chutes.length) {
    const inner = own(ribbonMaterial(2.6, 0.55, 1.5));
    const outer = own(ribbonMaterial(-1.7, 0.38, 0.95));
    const ringMat = own(ringMaterial());
    const ringGeo = own(new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2));
    for (const c of chutes) {
      const h = c.top - c.base + 3;
      const geoA = own(new THREE.CylinderGeometry(c.r * 0.8, c.r * 0.55, h, 28, 1, true).translate(0, h / 2, 0));
      const geoB = own(new THREE.CylinderGeometry(c.r * 1.15, c.r * 0.9, h, 28, 1, true).translate(0, h / 2, 0));
      const a = new THREE.Mesh(geoA, inner);
      const b = new THREE.Mesh(geoB, outer);
      const gy = Math.max(c.base, groundY(c.pos.x, c.pos.y)) - 0.2;
      a.position.set(c.pos.x, gy, -c.pos.y);
      b.position.copy(a.position);
      a.renderOrder = b.renderOrder = 3;
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.scale.setScalar(c.r * 3.4);
      ring.position.set(c.pos.x, groundY(c.pos.x, c.pos.y) + 0.08, -c.pos.y);
      group.add(a, b, ring);
    }
    // Motes and leaves: 70 per chute, one draw.
    const N = 110;
    const pos = new Float32Array(chutes.length * N * 3);
    const seed = new Float32Array(chutes.length * N * 4);
    const col = new Float32Array(chutes.length * N * 3);
    chutes.forEach((c, ci) => {
      const gy = Math.max(c.base, groundY(c.pos.x, c.pos.y));
      for (let k = 0; k < N; k++) {
        const i = ci * N + k;
        pos.set([c.pos.x, gy, -c.pos.y], i * 3);
        const leaf = k % 5 === 0 ? 1 : 0;
        seed.set([c.r * 0.95, c.top - gy + 2, hash(i + 0.5), leaf], i * 4);
        col.set(leaf ? [0.55, 0.85, 0.42] : [0.85, 1.0, 0.97], i * 3);
      }
    });
    const mg = own(new THREE.BufferGeometry());
    mg.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    mg.setAttribute('aSeed', new THREE.BufferAttribute(seed, 4));
    mg.setAttribute('aCol', new THREE.BufferAttribute(col, 3));
    const motes = new THREE.Points(mg, own(moteMaterial()));
    motes.frustumCulled = false;
    motes.renderOrder = 4;
    group.add(motes);
  }

  // ---- platforms -------------------------------------------------------------------------------
  if (plats.length) {
    const kindOf = (p: PlatformDef): string => (p.kind && KIND_COL[p.kind] ? p.kind : 'rock');
    const mats = new Map<string, THREE.MeshToonMaterial>();
    const mat = (hex: number, emissive = 0): THREE.MeshToonMaterial => {
      const k = `${hex}|${emissive}`;
      let m = mats.get(k);
      if (!m) mats.set(k, (m = own(ownedToonMaterial({ color: hex, emissive }))));
      return m;
    };
    const hullMat = own(new THREE.MeshBasicMaterial({ color: 0x2a2230, side: THREE.BackSide }));
    const body = own(rockBody());
    const disc = own(new THREE.CylinderGeometry(1, 1, 0.12, 14).translate(0, -0.06, 0));
    const plank = own(new THREE.BoxGeometry(2, 0.12, 0.34).translate(0, -0.06, 0));
    const pole = own(new THREE.CylinderGeometry(0.09, 0.11, 1, 6).translate(0, -0.5, 0));
    const shard = own(new THREE.OctahedronGeometry(0.5, 0).scale(0.45, 1.6, 0.45));
    const slab = own(new THREE.BoxGeometry(2, 0.5, 2).translate(0, -0.25, 0));
    const stub = own(new THREE.CylinderGeometry(0.28, 0.32, 1.4, 8).translate(0, 0.7, 0));
    const column = own(new THREE.CylinderGeometry(1, 0.86, 1, 9).translate(0, -0.5, 0));
    const clear = (p: PlatformDef): number => p.top - groundY(p.pos.x, p.pos.y);
    const low = (p: PlatformDef): boolean => clear(p) < 3.2;
    const M = new THREE.Matrix4();
    const Q = new THREE.Quaternion();
    const S = new THREE.Vector3();
    const P = new THREE.Vector3();
    const AY = new THREE.Vector3(0, 1, 0);
    const yaw = (p: PlatformDef): number => hash(p.pos.x * 0.13 + p.pos.y * 0.71) * Math.PI * 2;
    const at = (p: PlatformDef, dx: number, dy: number, dz: number, sx: number, sy: number, sz: number, rot = yaw(p)): THREE.Matrix4 => {
      Q.setFromAxisAngle(AY, rot);
      P.set(dx, dy, dz).applyQuaternion(Q).add(S.set(p.pos.x, p.top, -p.pos.y));
      return M.compose(P, Q, S.set(sx, sy, sz));
    };
    const parts: PartSpec[] = [
      // Rock / crystal bodies (the ruin slab and the scaffold have their own).
      // A floating rock high up; a low step is a rock column standing on the ground.
      { geo: body, mat: mat(0x8a8174), count: (p) => (['rock', 'crystal'].includes(kindOf(p)) && !low(p) ? 1 : 0), place: (p, m) => (m.copy(at(p, 0, 0, 0, p.r * 1.02, 0.6 + p.r * 0.45, p.r * 1.02)), true), hull: true },
      { geo: column, mat: mat(0x8a8174), count: (p) => (['rock', 'crystal'].includes(kindOf(p)) && low(p) ? 1 : 0), place: (p, m) => (m.copy(at(p, 0, 0, 0, p.r * 1.0, clear(p) + 0.3, p.r * 1.0)), true), hull: true },
      // Moss / pale tops (the walkable face).
      { geo: disc, mat: mat(0x6fa452), count: (p) => (kindOf(p) === 'rock' ? 1 : 0), place: (p, m) => (m.copy(at(p, 0, 0.02, 0, p.r * 0.97, 1, p.r * 0.97)), true) },
      { geo: disc, mat: mat(0xcfc8dd), count: (p) => (kindOf(p) === 'crystal' ? 1 : 0), place: (p, m) => (m.copy(at(p, 0, 0.02, 0, p.r * 0.97, 1, p.r * 0.97)), true) },
      // Crystals crowning the crystal rocks (to one side, clear of the middle) and hanging under them.
      { geo: shard, mat: mat(0x9fe3f0, 0x3a7f8c), count: (p) => (kindOf(p) === 'crystal' ? 3 : 0), place: (p, m, k) => (m.copy(at(p, Math.cos(k * 2.1) * p.r * 0.8, k === 2 ? -1.1 : 0.3, Math.sin(k * 2.1) * p.r * 0.8, 1, k === 2 ? 1.2 : 0.9 - 0.2 * k, 1)), true) },
      // Scaffolds: a plank deck on four poles down to the ground.
      { geo: plank, mat: mat(0xb08a58), count: (p) => (kindOf(p) === 'scaffold' ? 5 : 0), place: (p, m, k) => (m.copy(at(p, 0, 0, (k - 2) * 0.36 * p.r * 0.5, p.r, 1, p.r * 0.95)), true), hull: true },
      {
        geo: pole,
        mat: mat(0x6e5238),
        count: (p) => (kindOf(p) === 'scaffold' ? 4 : 0),
        place: (p, m, k) => {
          const a = yaw(p) + Math.PI / 4 + (k * Math.PI) / 2;
          const x = p.pos.x + Math.cos(a) * p.r * 0.8;
          const y = p.pos.y + Math.sin(a) * p.r * 0.8;
          const len = Math.max(0.3, p.top - groundY(x, y) + 0.2);
          m.compose(P.set(x, p.top, -y), Q.identity(), S.set(1, len, 1));
          return true;
        },
      },
      // Ruins: a floating stone slab with a broken pillar stub at its rim.
      { geo: slab, mat: mat(0xc4bba8), count: (p) => (kindOf(p) === 'ruin' ? 1 : 0), place: (p, m) => (m.copy(at(p, 0, 0, 0, p.r * 0.72, 1, p.r * 0.72)), true), hull: true },
      { geo: body, mat: mat(0x9b917f), count: (p) => (kindOf(p) === 'ruin' ? 1 : 0), place: (p, m) => (m.copy(at(p, 0, -0.45, 0, p.r * 0.9, 0.7, p.r * 0.9)), true) },
      { geo: stub, mat: mat(0xd9d2c0), count: (p) => (kindOf(p) === 'ruin' ? 1 : 0), place: (p, m) => (m.copy(at(p, p.r * 0.78, 0, 0, 1, 0.7 + hash(p.top) * 0.6, 1)), true) },
    ];
    for (const part of parts) {
      const n = plats.reduce((a, p) => a + part.count(p), 0);
      if (!n) continue;
      const mesh = new THREE.InstancedMesh(part.geo, part.mat, n);
      const hull = part.hull ? new THREE.InstancedMesh(part.geo, hullMat, n) : null;
      let i = 0;
      const m = new THREE.Matrix4();
      const big = new THREE.Matrix4();
      for (const p of plats)
        for (let k = 0; k < part.count(p); k++) {
          if (!part.place(p, m, k)) continue;
          mesh.setMatrixAt(i, m);
          if (hull) hull.setMatrixAt(i, big.copy(m).multiply(new THREE.Matrix4().makeScale(1.05, 1.03, 1.05)));
          i++;
        }
      mesh.count = i;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      group.add(mesh);
      if (hull) {
        hull.count = i;
        group.add(hull);
      }
    }
  }

  return {
    group,
    update(): void {
      /* stateless: the shaders animate from U.uTime */
    },
    dispose(): void {
      group.removeFromParent();
      for (const o of owned) o.dispose();
    },
  };
}

/** +glide: the nearest chute to p (sim plane), for the hum. */
export function nearestChute(chutes: readonly UpdraftDef[], x: number, y: number): { c: UpdraftDef; d: number } | null {
  let best: { c: UpdraftDef; d: number } | null = null;
  for (const c of chutes) {
    const d = Math.hypot(c.pos.x - x, c.pos.y - y);
    if (!best || d < best.d) best = { c, d };
  }
  return best;
}
